/**
 * Booking Modal Component
 * Handles final booking submission with accommodation requests
 */

import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { CustomSafariItinerary } from '../../types/customSafari';
import { AccommodationSelection } from '../../types/accommodation';
import { bookingRequestService } from '../../services/bookingRequestService';
import { customSafariService } from '../../services/customSafariService';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  itinerary: CustomSafariItinerary;
  accommodationSelections: AccommodationSelection[];
  onBookingSuccess?: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  itinerary,
  accommodationSelections,
  onBookingSuccess
}) => {
  const { actualTheme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const [step, setStep] = useState<'review' | 'processing' | 'success' | 'error'>('review');
  const [error, setError] = useState<string | null>(null);
  const [bookingRequests, setBookingRequests] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      setStep('review');
      setError(null);
      setBookingRequests([]);
    }
  }, [isOpen]);

  const handleConfirmBooking = async () => {
    if (!user || !isAuthenticated) {
      setError('Please sign in to complete your booking');
      return;
    }

    if (!itinerary) {
      setError('Invalid itinerary');
      return;
    }

    try {
      setStep('processing');
      setError(null);

      // Generate safari booking ID
      const safariBookingId = `safari_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create booking requests for all accommodation selections
      const requests = await bookingRequestService.createBookingRequestsForSafari(
        safariBookingId,
        user.id,
        itinerary,
        accommodationSelections,
        {
          name: user.name,
          email: user.email,
          phone: user.phone || '',
          specialRequests: undefined
        }
      );

      setBookingRequests(requests);
      setStep('success');

      // Call success callback
      if (onBookingSuccess) {
        setTimeout(() => {
          onBookingSuccess();
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error creating booking:', error);
      setError(error.message || 'Failed to create booking. Please try again.');
      setStep('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" style={{ pointerEvents: 'auto' }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Modal */}
      <div className={`absolute inset-0 flex items-center justify-center p-4 ${
        actualTheme === 'dark' ? 'bg-slate-900/50' : 'bg-white/50'
      }`}>
        <div className={`rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
          actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${
            actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
          }`}>
            <h2 className={`text-2xl font-bold ${
              actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
              Confirm Booking
            </h2>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                actualTheme === 'dark'
                  ? 'text-slate-400 hover:bg-slate-700 hover:text-white'
                  : 'text-neutral-600 hover:bg-neutral-100 hover:text-primary-800'
              }`}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'review' && (
              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>
                    Safari Details
                  </h3>
                  <div className={`rounded-lg p-4 ${
                    actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-neutral-50'
                  }`}>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className={`${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                          Safari Name:
                        </span>
                        <span className={`font-medium ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {itinerary.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                          Tours:
                        </span>
                        <span className={`font-medium ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {itinerary.tours.length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                          Duration:
                        </span>
                        <span className={`font-medium ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {itinerary.totalDuration} days
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                          Total Price:
                        </span>
                        <span className={`font-bold text-lg ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`}>
                          {itinerary.totalPrice.currency} {itinerary.totalPrice.total.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {accommodationSelections.length > 0 && (
                  <div>
                    <h3 className={`text-lg font-semibold mb-4 ${
                      actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>
                      Accommodation Requests
                    </h3>
                    <div className="space-y-3">
                      {accommodationSelections.map((selection, index) => (
                        <div
                          key={index}
                          className={`rounded-lg p-4 border ${
                            actualTheme === 'dark'
                              ? 'bg-slate-700/50 border-slate-600'
                              : 'bg-neutral-50 border-neutral-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <BuildingOfficeIcon className={`w-5 h-5 mt-0.5 ${
                              actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                            }`} />
                            <div className="flex-1">
                              <div className={`font-medium mb-1 ${
                                actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                {selection.location}
                              </div>
                              <div className={`text-sm ${
                                actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                              }`}>
                                {selection.nights} night{selection.nights !== 1 ? 's' : ''}
                                {selection.providerId ? ' • Specific provider selected' : ' • Let SightTrails choose'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {error && (
                  <div className={`rounded-lg p-4 border ${
                    actualTheme === 'dark'
                      ? 'bg-red-900/20 border-red-800 text-red-300'
                      : 'bg-red-50 border-red-200 text-red-600'
                  }`}>
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{error}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-neutral-200 dark:border-slate-700">
                  <button
                    onClick={onClose}
                    className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                      actualTheme === 'dark'
                        ? 'bg-slate-700 hover:bg-slate-600 text-white'
                        : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmBooking}
                    className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                      actualTheme === 'dark'
                        ? 'bg-primary-700 hover:bg-primary-600 text-white'
                        : 'bg-primary-800 hover:bg-primary-700 text-white'
                    }`}
                  >
                    Confirm Booking
                  </button>
                </div>
              </div>
            )}

            {step === 'processing' && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
                <p className={`text-lg font-medium mb-2 ${
                  actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>
                  Processing your booking...
                </p>
                <p className={`text-sm ${
                  actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                }`}>
                  Sending accommodation requests to providers
                </p>
              </div>
            )}

            {step === 'success' && (
              <div className="text-center py-12">
                <CheckCircleIcon className={`h-16 w-16 mx-auto mb-4 ${
                  actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                }`} />
                <h3 className={`text-2xl font-bold mb-2 ${
                  actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Booking Confirmed!
                </h3>
                <p className={`mb-6 ${
                  actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                }`}>
                  Your accommodation requests have been sent to providers.
                  You'll receive updates as they respond.
                </p>
                {bookingRequests.length > 0 && (
                  <div className={`rounded-lg p-4 mb-6 ${
                    actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-neutral-50'
                  }`}>
                    <p className={`text-sm font-medium mb-2 ${
                      actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                      {bookingRequests.length} accommodation request{bookingRequests.length !== 1 ? 's' : ''} sent
                    </p>
                  </div>
                )}
                <button
                  onClick={onClose}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    actualTheme === 'dark'
                      ? 'bg-primary-700 hover:bg-primary-600 text-white'
                      : 'bg-primary-800 hover:bg-primary-700 text-white'
                  }`}
                >
                  View My Accommodations
                </button>
              </div>
            )}

            {step === 'error' && (
              <div className="text-center py-12">
                <ExclamationTriangleIcon className={`h-16 w-16 mx-auto mb-4 ${
                  actualTheme === 'dark' ? 'text-red-400' : 'text-red-600'
                }`} />
                <h3 className={`text-2xl font-bold mb-2 ${
                  actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Booking Failed
                </h3>
                {error && (
                  <p className={`mb-6 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                    {error}
                  </p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                      actualTheme === 'dark'
                        ? 'bg-slate-700 hover:bg-slate-600 text-white'
                        : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                    }`}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => setStep('review')}
                    className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                      actualTheme === 'dark'
                        ? 'bg-primary-700 hover:bg-primary-600 text-white'
                        : 'bg-primary-800 hover:bg-primary-700 text-white'
                    }`}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;

